import React from 'react';
import styled from 'styled-components';
import { Body1, Body5, Container, SVG_LOADING_ICON_TYPES, SvgLoadingIcon } from '@ui';
import CheckCircle from '@assets/icons/svgs/CheckCircle';
import { useColorTheme } from '@hooks';
import { CollapseButton, CollapseButtonIconWrapper } from '@ui/buttons/CollapseButton';
import { useUploadStatus, useUploadStatusTemplateGeneration } from '@hooks/features/useUploadStatus';
import { createTimeInstance } from '@utils/time';
import { Working } from '@ui/Working';
import { FileDetail } from '@containers/UI/FileDetail';
import { useFileDetail } from '@hooks/features/useFileDetail';
import { DocRowActionsMenu } from '@containers/application/DocRowActionsMenu';

const Wrapper = styled.div`
  padding-top: 16px;
`;
const SectionWrapper = styled.div`
  padding: 0 16px 16px 16px;
`;
const SectionHeading = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;
const SectionHeadingWrapper = styled.div`
  padding: 2px 0 8px 0;
  text-transform: uppercase;

  font-size: 12px;
  line-height: 14px;
  font-weight: bold;
`;
const DocRowWrapper = styled.tr`
  padding: 8px 8px;
  border-bottom: 1px solid ${({ theme }) => theme.themeColors.lowContrastBorder};
`;
const CompletedDocCellsRowWrapper = styled.tr`
  padding: 8px 8px;
`;
const CollapseButtonOverrideStyle = styled.div`
  ${CollapseButtonIconWrapper} {
    padding-bottom: 0;
  }
`;
const DocRowCell = styled.td`
  padding: 8px 8px;
  border-bottom: ${({ HideBorder, theme }) =>
    HideBorder ? 'none' : `1px solid ${theme.themeColors.lowContrastBorder}`};
`;

const ProgressBarWrapper = styled.div`
  width: 120px;
  height: 15px;
  background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressBarFill = styled.div`
  height: 100%;
  background-color: ${({ theme }) => theme.themeColors.primaryCtaButton};
  border-radius: 1px;
  transition: width 0.3s ease-in-out;
  width: ${({ percent }) => percent}%;
`;
const CheckWrapper = styled.div`
  svg {
    height: 24px;
    width: 24px;

    path {
      fill: ${({ theme }) => theme.themeColors.primaryCtaButton};
    }
  }
`;
function expectedTimeToUpload(fileSizeBytes, fileName = '', filter = null) {
  // Special handling for template generation
  if (filter === 'template_generation') {
    return 1000 * 60 * 20; // 20 minutes
  }

  const fileSizeMB = fileSizeBytes / 1024 / 1024;
  // Check if file is an Excel file (.xls or .xlsx)
  const isExcelFile =
    fileName &&
    (fileName.toLowerCase().endsWith('.xls') ||
      fileName.toLowerCase().endsWith('.xlsx') ||
      fileName.toLowerCase().endsWith('.xlsm') ||
      fileName.toLowerCase().endsWith('.xlsb'));

  if (isExcelFile) {
    // Special handling for Excel files
    // Minimum 10 minutes, scaling up to 60 minutes for 100MB file
    const minTime = 10; // minutes
    const maxTime = 60; // minutes
    const maxSize = 100; // MB

    if (fileSizeMB <= 0) {
      return 1000 * 60 * minTime;
    }

    if (fileSizeMB >= maxSize) {
      return 1000 * 60 * maxTime;
    }

    // Linear interpolation from 0MB (10 min) to 100MB (60 min)
    const interpolatedTime = minTime + (fileSizeMB / maxSize) * (maxTime - minTime);
    return 1000 * 60 * interpolatedTime;
  } else {
    // Original logic for non-Excel files
    // Interpolate between 1MB (2 min) and 8MB (60 min)
    const minTime = 2; // minutes
    const maxTime = 60; // minutes
    const minSize = 1; // MB
    const maxSize = 8; // MB

    if (fileSizeMB < minSize) {
      return 1000 * 60 * minTime;
    }

    if (fileSizeMB > maxSize) {
      return 1000 * 60 * maxTime;
    }

    const interpolatedTime = minTime + ((fileSizeMB - minSize) / (maxSize - minSize)) * (maxTime - minTime);

    return 1000 * 60 * interpolatedTime;
  }
}

function capProgress(progress, max) {
  if (progress > max) {
    return max;
  }
  return progress;
}

function calculateProgress(fileSizeBytes, uploadFinishedAt, fileName = '', filter = null) {
  if (!fileSizeBytes || !uploadFinishedAt) {
    return 1;
  }

  const timeUploading = createTimeInstance();
  const timeUploadFinished = createTimeInstance(uploadFinishedAt);
  const timeElapsed = timeUploading.diff(timeUploadFinished);
  const expectedTime = expectedTimeToUpload(fileSizeBytes, fileName, filter);
  const progress = (timeElapsed / expectedTime) * 100;
  // console.log('eT', expectedTime, fileName, filter);
  return Math.round(capProgress(progress, 85));
}

const ProgressBarTextWrapper = styled.div`
  padding-left: 8px;
  width: 44px;
  text-align: right;
  overflow: hidden;
`;

const ProgressBar = ({ value = 0 }) => {
  return (
    <Container row verticallyCenter>
      <ProgressBarWrapper>
        <ProgressBarFill percent={value} />
      </ProgressBarWrapper>
      <ProgressBarTextWrapper left={8}>
        <Body5 isLowContrast>{value}%</Body5>
      </ProgressBarTextWrapper>
    </Container>
  );
};

const DocRowPending = ({ doc, filter }) => {
  const [progress, setProgress] = React.useState(
    calculateProgress(doc.file_size_bytes, doc.upload_finished_at, doc.title, filter)
  );

  React.useEffect(() => {
    const interval = setInterval(() => {
      setProgress(calculateProgress(doc.file_size_bytes, doc.upload_finished_at, doc.title, filter));
    }, 3000);

    return () => clearInterval(interval);
  }, [doc.file_size_bytes, doc.upload_finished_at, filter]);

  return (
    <DocRowWrapper>
      <DocRowCell style={{ width: '100%' }}>
        <Body5>{doc.title}</Body5>
      </DocRowCell>
      <DocRowCell>
        <Container row alignRight verticallyCenter>
          <Container right={16}>
            <Body5 isLowContrast>Processing...</Body5>
          </Container>
          <Container row verticallyCenter left={24} right={8} vertical={8}>
            <ProgressBar value={progress} />
          </Container>
        </Container>
      </DocRowCell>
    </DocRowWrapper>
  );
};

const DocCompletedFileDetails = ({ docId }) => {
  const { data, error } = useFileDetail(docId);

  if (error) {
    return <Body5>Something went wrong getting file details, please try again later...</Body5>;
  }

  if (!data) {
    return <Working message="Loading file details..." />;
  }

  return (
    <Container>
      <FileDetail data={data} />
    </Container>
  );
};

const DocRowCompleted = ({ doc, filter }) => {
  const [collapsed, setCollapsed] = React.useState(true);

  return (
    <>
      <CompletedDocCellsRowWrapper>
        <DocRowCell HideBorder={!collapsed}>
          <CollapseButtonOverrideStyle>
            <CollapseButton isCollapsed={collapsed} setCollapsed={setCollapsed} />
          </CollapseButtonOverrideStyle>
        </DocRowCell>
        <DocRowCell HideBorder={!collapsed} style={{ width: '100%' }}>
          <div style={{ cursor: 'pointer' }} onClick={() => setCollapsed(!collapsed)}>
            <Body5>{doc.title}</Body5>
          </div>
        </DocRowCell>
        <DocRowCell HideBorder={!collapsed} style={{ width: '100%' }}>
          <Container row verticallyCenter alignRight right={24}>
            {filter === 'template_generation' ? <div /> : <DocRowActionsMenu doc={doc} />}
            <Container left={24}>
              <CheckWrapper>
                <CheckCircle />
              </CheckWrapper>
            </Container>
          </Container>
        </DocRowCell>
      </CompletedDocCellsRowWrapper>
      <CompletedDocCellsRowWrapper>
        {collapsed ? null : (
          <DocRowCell colSpan={3}>
            <DocCompletedFileDetails docId={doc.doc_id} />
          </DocRowCell>
        )}
      </CompletedDocCellsRowWrapper>
    </>
  );
};
const DocRowErrored = ({ doc }) => {
  return (
    <DocRowWrapper>
      <DocRowCell style={{ width: '100%' }}>
        <Body5>{doc.title}</Body5>
      </DocRowCell>
      <DocRowCell style={{ width: '100%' }}></DocRowCell>
    </DocRowWrapper>
  );
};

const Section = ({ name, docs, startingOpen, filter }) => {
  const [collapsed, setCollapsed] = React.useState(!startingOpen);

  if (!docs || docs.length === 0) {
    return null;
  }

  return (
    <SectionWrapper>
      <SectionHeading>
        <SectionHeadingWrapper>{name}</SectionHeadingWrapper>
        <CollapseButton isCollapsed={collapsed} setCollapsed={setCollapsed} />
      </SectionHeading>
      {collapsed ? null : (
        <table>
          {docs.map((d) =>
            name === 'Completed' ? (
              <DocRowCompleted key={d.id} doc={d} filter={filter} />
            ) : name === 'Errored' ? (
              <DocRowErrored key={d.id} doc={d} />
            ) : (
              <DocRowPending key={d.id} doc={d} filter={filter} />
            )
          )}
        </table>
      )}
    </SectionWrapper>
  );
};

const empty = {
  completed: [],
  pending: [],
  errored: [],
};

export function DocumentUploadStatusTemplateGeneration({ hideIfNoDocs = false }) {
  const filter = 'template_generation';
  const colorTheme = useColorTheme();
  const { isInitializing, data } = useUploadStatusTemplateGeneration();
  const { completed, pending, errored } = data || empty;

  if (hideIfNoDocs && (isInitializing || (completed.length === 0 && pending.length === 0 && errored.length === 0))) {
    return null;
  }

  return (
    <Wrapper>
      <Container bottom={16}>
        <Body1 bold>Current Uploads Status</Body1>
      </Container>
      {isInitializing ? (
        <Container row verticallyCenter>
          <SvgLoadingIcon type={SVG_LOADING_ICON_TYPES.bars} color={colorTheme.text} />
          <Container left={8}>
            <Body5>Checking document uploads...</Body5>
          </Container>
        </Container>
      ) : null}

      {!isInitializing && completed.length === 0 && pending.length === 0 && errored.length === 0 ? (
        <Container bottom={16}>
          <Body5>No uploads in progress</Body5>
        </Container>
      ) : null}

      <Section name={'Pending'} docs={pending || []} startingOpen filter={filter} />
      <Section name={'Completed'} docs={completed || []} startingOpen filter={filter} />
      <Section name={'Errored'} docs={errored || []} startingOpen filter={filter} />
    </Wrapper>
  );
}

export function DocumentUploadStatus() {
  const colorTheme = useColorTheme();
  const { isInitializing, data } = useUploadStatus();
  const { completed, pending, errored } = data || empty;

  return (
    <Wrapper>
      <Container bottom={16}>
        <Body1 bold>Current Uploads Status</Body1>
      </Container>
      {isInitializing ? (
        <Container row verticallyCenter>
          <SvgLoadingIcon type={SVG_LOADING_ICON_TYPES.bars} color={colorTheme.text} />
          <Container left={8}>
            <Body5>Checking document uploads...</Body5>
          </Container>
        </Container>
      ) : null}

      {!isInitializing && completed.length === 0 && pending.length === 0 && errored.length === 0 ? (
        <Container bottom={16}>
          <Body5>No uploads in progress</Body5>
        </Container>
      ) : null}

      <Section name={'Pending'} docs={pending || []} startingOpen />
      <Section name={'Completed'} docs={completed || []} startingOpen />
      <Section name={'Errored'} docs={errored || []} startingOpen />
    </Wrapper>
  );
}
